import React from 'react'
import styles from './OverviewHeight.module.scss'
import TitleFlex from 'components/cases/common/TitleFlex'
import ImageWithCaption from 'components/cases/common/Interview/fragments/ImageWithCaption'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

const tweenVars: gsap.TimelineVars = {
  y: 50,
  duration: 1,
  opacity: 0,
  ease: Power4.easeOut,
}

export type OverviewImageItem = {
  src: string
  alt: string
  text?: string | JSX.Element
  size?: 'm' | 's' | 'ss'
}

export type OverviewItems = {
  text?: string | JSX.Element
  source?: string | JSX.Element
  nonMargin?: boolean
  img?: string
  imgsp?: string
  isSubTextGrey?: boolean
  img_nonMargin?: boolean
  image?: OverviewImageItem[]
}

export type IssueSolutionItems = {
  issues?: { title?: string; text?: string; text2?: string | React.ReactNode }[]
  solutions?: { title?: string; text?: string; text2?: string | React.ReactNode }[]
  outcomes?: { title?: string; text?: string; text2?: string | React.ReactNode }[]
}

type Props = {
  overviewItem: OverviewItems[]
  issueSolutionItems?: IssueSolutionItems[]
  overviewTitle?: string | JSX.Element
}

const Overview: React.FC<Props> = ({ overviewTitle, overviewItem, issueSolutionItems }) => {
  const wrapperRef = useRef<HTMLElement>(null)
  const textContainerRef = useRef<HTMLDivElement>(null)
  const boxesRefs = useRef<HTMLDivElement[]>([])

  useEffect(() => {
    const tl = gsap.timeline()

    tl.from(textContainerRef.current, { ...tweenVars, y: 50 }, 0.4)

    boxesRefs.current.forEach((box, index) => {
      tl.from(box, { opacity: 0, y: 50, duration: 1, ease: Power4.easeOut }, `-=${0.8 + index * 0.1}`)
    })

    ScrollTrigger.create({
      animation: tl,
      trigger: wrapperRef.current,
      start: 'top 90%',
      toggleActions: 'play none none play',
    })

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
      tl.kill()
    }
  }, [overviewItem, issueSolutionItems])

  const sectionTitle: { title: string; sub_title: string } = {
    title: 'Overview',
    sub_title: '概要',
  }

  return (
    <section className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.content}>
        <TitleFlex sectionTitle={sectionTitle} />
        <div className={styles.text_container} ref={textContainerRef}>
          <h3 className={styles.overviewttl}>{overviewTitle}</h3>
          {overviewItem.map((item, index) => (
            <div key={index}>
              {item.text && <p className={styles.text}>{item.text}</p>}
              {item.source && <p className={styles.source}>{item.source}</p>}
              {item.image && item.image.length > 0 && (
                <div className={styles.overview_images}>
                  {item.image.map((image, imageIdx) => (
                    <ImageWithCaption
                      key={imageIdx}
                      src={image.src}
                      alt={image.alt}
                      text={image.text}
                      size={image.size}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {issueSolutionItems?.map((item, index) => (
        <div key={index} className={styles.boxes} ref={(el) => (boxesRefs.current[index] = el)}>
          <div className={styles.box}>
            <h4 className={styles.box_title}>クライアントの課題</h4>
            {(item.issues || []).map((issue, idx) => (
              <div key={idx} className={styles.box_contents}>
                {issue.title && <h5 className={styles.box_contents_ttl}>{issue.title}</h5>}
                {issue.text && <p className={styles.box_contents_txt}>{issue.text}</p>}
                {issue.text2 && <p className={styles.box_contents_txt2}>{issue.text2}</p>}
              </div>
            ))}
          </div>
          <div className={styles.box}>
            <h4 className={styles.box_title}>Aitriaの解決方法</h4>
            {(item.solutions || []).map((solution, idx) => (
              <div key={idx} className={styles.box_contents}>
                {solution.title && (
                  <h5 className={`${styles.box_contents_ttl} ${styles.box_contents_ttl__solution}`}>
                    {solution.title}
                  </h5>
                )}
                {solution.text && <p className={styles.box_contents_txt}>{solution.text}</p>}
                {solution.text2 && (
                  <p className={`${styles.box_contents_txt2} ${styles.box_contents_txt2__solution}`}>
                    {solution.text2}
                  </p>
                )}
              </div>
            ))}
          </div>
          <div className={styles.box}>
            <h4 className={styles.box_title}>プロジェクトの成果</h4>
            {(item.outcomes || []).map((outcome, idx) => (
              <div key={idx} className={styles.box_contents}>
                {outcome.title && (
                  <h5 className={`${styles.box_contents_ttl} ${styles.box_contents_ttl__solution}`}>{outcome.title}</h5>
                )}
                {outcome.text && <p className={styles.box_contents_txt}>{outcome.text}</p>}
                {outcome.text2 && (
                  <p className={`${styles.box_contents_txt2} ${styles.box_contents_txt2__solution}`}>{outcome.text2}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  )
}

export default Overview
